import React, { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import UnifiedChat from './chat/UnifiedChat';
import MessageInbox from './MessageInbox';
import TutorialOverlay from './tutorial/TutorialOverlay';
import TutorialAutoTrigger from './tutorial/TutorialAutoTrigger';
import WelcomeOverlay from './WelcomeOverlay';
import { User, DoorOpen, Gamepad2, Store, Settings, Trophy, Volume2, ShoppingCart, Copy, Check, Bed } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import ExperienceModal from './ExperienceModal';
import BuyExperienceModal from './BuyExperienceModal';
import WalletHistoryModal from './WalletHistoryModal';
import QrPaymentLinkModal from './QrPaymentLinkModal';
import TransportRideModal from './TransportRideModal';
import AccountSwitchModal from './AccountSwitchModal';
import GlobalMusicPlayer from './GlobalMusicPlayer';
import { usePushNotifications } from '../hooks/usePushNotifications';
import IosInstallPromptModal from './IosInstallPromptModal';
import useEscapeKey from '../hooks/useEscapeKey';
import { useStoreStaffRoles, canAccessStoreDashboard, canAccessStorePOS } from '../utils/storePermissions';
import { resolveImageUrlBusted, resolveImageUrl } from '../utils/urlHelper';
import toast from 'react-hot-toast';

// Wallet Modals
import RedeemModal from './RedeemModal';
import SendFiresModal from './SendFiresModal';
import BuyFiresModal from './BuyFiresModal';
import ReceiveFiresModal from './ReceiveFiresModal';
import GlobalCartDrawer from './GlobalCartDrawer';

const Layout = () => {
  const { user, isAdmin, isTote, updateUser } = useAuth();
  const { totalItems, itemsByStore, globalTotal } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [showGlobalCart, setShowGlobalCart] = useState(false);
  const [showExperienceModal, setShowExperienceModal] = useState(false);
  const [showBuyExperienceModal, setShowBuyExperienceModal] = useState(false);
  const [showWalletHistoryModal, setShowWalletHistoryModal] = useState(false);
  const [walletHistoryInitialTab, setWalletHistoryInitialTab] = useState('fires');
  const [showBcvCalculator, setShowBcvCalculator] = useState(false);
  const [bcvCalculatorInput, setBcvCalculatorInput] = useState('');
  const [bcvCalculatorMode, setBcvCalculatorMode] = useState('multiply');
  const [bcvCopied, setBcvCopied] = useState(false);

  const handleCopyBcvResult = async (resultVal, isValid) => {
    if (!isValid || !resultVal || resultVal <= 0) return;
    const toCopy = Number(resultVal).toFixed(2);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(toCopy);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = toCopy;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setBcvCopied(true);
      toast.success(`Monto copiado: ${toCopy}`);
      setTimeout(() => setBcvCopied(false), 2000);
    } catch (err) {
      toast.error('No se pudo copiar');
    }
  };
  const [showQrPaymentModal, setShowQrPaymentModal] = useState(false);
  const [showTransportRideModal, setShowTransportRideModal] = useState(false);
  const [transportAutoLocateToken, setTransportAutoLocateToken] = useState(0);
  const [showGreenButtonPickerModal, setShowGreenButtonPickerModal] = useState(false);
  const [showAccountSwitchModal, setShowAccountSwitchModal] = useState(false);
  const [showMusicPlayer, setShowMusicPlayer] = useState(false);
  const [musicUiState, setMusicUiState] = useState({ playing: false, volume: 0, hasTrack: false });
  const [showPushPermissionModal, setShowPushPermissionModal] = useState(false);
  const [showIosInstallModal, setShowIosInstallModal] = useState(false);
  
  // Wallet states
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [showSendFires, setShowSendFires] = useState(false);
  const [showBuyFires, setShowBuyFires] = useState(false);
  const [showReceiveFires, setShowReceiveFires] = useState(false);
  const greenButtonLongPressTimeoutRef = useRef(null);
  const greenButtonLongPressTriggeredRef = useRef(false);

  useEscapeKey(() => {
    setShowBcvCalculator(false);
    setShowMusicPlayer(false);
  }, showBcvCalculator || showMusicPlayer);

  useEscapeKey(() => {
    setShowGreenButtonPickerModal(false);
  }, showGreenButtonPickerModal);

  useEscapeKey(() => {
    setShowPushPermissionModal(false);
    setShowIosInstallModal(false);
  }, showPushPermissionModal || showIosInstallModal);

  const [bingoGraceInfo, setBingoGraceInfo] = useState(null);
  const [bingoGraceOffsetMs, setBingoGraceOffsetMs] = useState(0);
  const [bingoGraceRemainingMs, setBingoGraceRemainingMs] = useState(0);
  const mainRef = useRef(null);
  const lastBingoGraceToastRef = useRef(null);

  // Initialize push notifications
  const { permissionStatus, tokenRegistered, isIOSPromptNeeded, requestPermissionAndRegister } = usePushNotifications();

  // Show push permission modal if needed
  useEffect(() => {
    if (permissionStatus === 'default' && user && typeof window !== 'undefined') {
      const shown = window.localStorage.getItem('push_permission_modal_shown');
      if (!shown) {
        setShowPushPermissionModal(true);
      }
    }
  }, [permissionStatus, user]);

  const isStoreDashboardRoute =
    typeof location?.pathname === 'string' &&
    location.pathname.startsWith('/store/') &&
    location.pathname.includes('/dashboard');

  const storeRouteMatch =
    typeof location?.pathname === 'string'
      ? location.pathname.match(/^\/store\/([^/]+)/)
      : null;
  const activeStoreSlug = storeRouteMatch?.[1] ? decodeURIComponent(storeRouteMatch[1]) : '';
  const isStoreRoute = !!activeStoreSlug;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isStoreDashboardRoute) {
      window.__MX_DISABLE_INBOX_PREFETCH__ = true;
      return;
    }
    if (window.__MX_DISABLE_INBOX_PREFETCH__) {
      delete window.__MX_DISABLE_INBOX_PREFETCH__;
    }
  }, [isStoreDashboardRoute]);

  const { data: activeStoreBranding } = useQuery({
    queryKey: ['layout-store-branding', activeStoreSlug],
    enabled: isStoreRoute,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const response = await axios.get(`/api/store/public/${encodeURIComponent(activeStoreSlug)}?nobump=1`);
      return response.data?.store || null;
    }
  });

  const isPosRoute =
    typeof location?.pathname === 'string' &&
    /^\/store\/[^/]+\/pos(\/|$)/.test(location.pathname);

  const isLodgingStore =
    activeStoreBranding?.store_type === 'hospedaje' ||
    activeStoreBranding?.store_type === 'hotel';

  // Fetch balance en tiempo real
  const { data: balanceData } = useQuery({
    queryKey: ['header-balance', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const response = await axios.get('/api/economy/balance', { params: { _ts: Date.now() } });
      // Actualizar user en contexto con nuevo balance
      if (response.data) {
        const { coins_balance: newCoins, fires_balance: newFires } = response.data;
        if (
          String(newCoins) !== String(user.coins_balance) ||
          String(newFires) !== String(user.fires_balance)
        ) {
          updateUser({
            ...user,
            coins_balance: newCoins,
            fires_balance: newFires
          });
        }
      }
      return response.data;
    },
    enabled: !!user,
    refetchInterval: 30000, // Refetch cada 30 segundos (antes: 3000ms causaba parpadeo agresivo)
    staleTime: 10000 // Cache por 10s para reducir requests innecesarios
  });

  const displayCoins = parseFloat(balanceData?.coins_balance ?? user?.coins_balance ?? 0);
  const displayFires = parseFloat(balanceData?.fires_balance ?? user?.fires_balance ?? 0);
  const displayExperience = user?.experience || 0;

  const { presenceStats } = useSocket();

  // Stats pÃºblicas para mostrar usuarios online
  const { data: publicStats } = useQuery({
    queryKey: ['public-stats-header'],
    queryFn: async () => {
      const response = await axios.get('/api/public/stats');
      return response.data?.data;
    },
    staleTime: 60 * 1000
  });

  const onlineUsers =
    (typeof presenceStats?.onlineNow === 'number' ? presenceStats.onlineNow : null) ??
    (publicStats?.users?.onlineNow ?? 0);

  const { data: rideRequestsForLayout } = useQuery({
    queryKey: ['transport-requests-ride-layout', user?.id],
    enabled: !!user?.id && !isStoreDashboardRoute,
    queryFn: async () => {
      const response = await axios.get('/api/transport/requests', { params: { kind: 'ride' } });
      return Array.isArray(response.data?.requests) ? response.data.requests : [];
    },
    refetchInterval: 15000,
    staleTime: 3000
  });

  const activeRideRequest = Array.isArray(rideRequestsForLayout)
    ? rideRequestsForLayout.find(
        (r) =>
          r &&
          r.kind === 'ride' &&
          r.requester_user_id === user?.id &&
          ['open', 'accepted', 'delivered_pending'].includes(r.status)
      )
    : null;

  // Tasa BCV / tasa operativa para mostrar precio de referencia en Bs
  const { data: fiatContext } = useQuery({
    queryKey: ['fiat-context'],
    queryFn: async () => {
      const response = await axios.get('/api/economy/fiat-context');
      return response.data;
    },
    staleTime: 60 * 1000
  });

  // Detectar query params para abrir modales de wallet/fuegos globalmente
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const open = params.get('open'); // 'send' | 'buy' | 'receive' | 'withdraw'

    if (tab === 'fires' || tab === 'coins') {
      setWalletHistoryInitialTab(tab);
      setShowWalletHistoryModal(true);
    }

    if (open === 'send') setShowSendFires(true);
    if (open === 'buy') setShowBuyFires(true);
    if (open === 'receive') setShowReceiveFires(true);
    if (open === 'withdraw') setShowRedeemModal(true);
    if (open === 'bcv_calculator') setShowBcvCalculator(true);
    if (open === 'experience') setShowExperienceModal(true);
    if (open === 'buy_experience') setShowBuyExperienceModal(true);
  }, [location.search]);

  const { refreshUser } = useAuth();

  let vesPerUsdt = null;
  if (fiatContext?.bcvRate && fiatContext.bcvRate.rate != null) {
    const bcvParsed = parseFloat(String(fiatContext.bcvRate.rate));
    if (Number.isFinite(bcvParsed) && bcvParsed > 0) {
      vesPerUsdt = bcvParsed;
    }
  }
  if (!vesPerUsdt && fiatContext?.operationalRate?.rate != null) {
    const opParsed = parseFloat(String(fiatContext.operationalRate.rate));
    if (Number.isFinite(opParsed) && opParsed > 0) {
      vesPerUsdt = opParsed;
    }
  }

  // Verificar permisos especiales: tote, tito y admin
  const username = (user?.username || '').toLowerCase().trim();
  const isToteUser = (typeof isTote === 'function' ? isTote() : false) || user?.roles?.includes('tote') || username === 'tote' || username === 'wilcnct' || user?.tg_id?.toString() === '1417856820';
  const isTitoUser = username === 'tito' || user?.roles?.includes('tito');
  const isAdminUser = (typeof isAdmin === 'function' ? isAdmin() : false) || user?.roles?.includes('admin') || user?.role === 'admin' || username === 'admin';
  const canSeeGamesAndRaffles = isToteUser || isTitoUser || isAdminUser;

  const homeSlug = user?.home_store_slug;
  const isGlobalAdmin = isToteUser || isAdminUser;
  const isStoreOwner = !!user?.home_store_id || !!user?.home_store_slug;
  const { data: homeStaffData, isLoading: homeStaffLoading } = useStoreStaffRoles(homeSlug);
  const canUseStorePosAction =
    !!homeSlug && (isGlobalAdmin || (!homeStaffLoading && canAccessStorePOS(homeStaffData)));
  const canUseStoreDashboardAction =
    !!homeSlug && (isGlobalAdmin || (!homeStaffLoading && canAccessStoreDashboard(homeStaffData)));

  const navItems = [
    { path: '/profile', icon: User, label: 'Perfil' },
    { path: '/lobby', icon: DoorOpen, label: 'Lobby' },
    ...(canSeeGamesAndRaffles ? [
      { path: '/games', icon: Gamepad2, label: 'Juegos', tutorialTarget: 'nav-games' },
      { path: '/raffles', icon: Trophy, label: 'Rifas' }
    ] : []),
    { path: '/market', icon: Store, label: 'Mercado' }
  ];

  // Añadir panel Admin para admins o para usuarios tote
  if (isAdminUser || isToteUser) {
    navItems.push({ path: '/admin', icon: Settings, label: 'Admin' });
  }

  const hideChat =
    (location.pathname.startsWith('/store/') &&
    location.pathname.includes('/pos')) ||
    location.pathname.startsWith('/moba/play') ||
    location.pathname.startsWith('/story/mission');

  const disableInboxPrefetch = isStoreDashboardRoute;

  const isOnBingoRoom =
    typeof location?.pathname === 'string' &&
    (location.pathname.startsWith('/bingo/v2/room/') || location.pathname.startsWith('/bingo/v2/play/'));

  const { data: activeBingoRooms = [] } = useQuery({
    queryKey: ['active-bingo-rooms-layout', user?.id],
    enabled: !!user?.id && canSeeGamesAndRaffles,
    queryFn: async () => {
      const res = await axios.get('/api/bingo/v2/active-rooms');
      return res.data?.rooms || [];
    },
    refetchInterval: 15000,
    staleTime: 3000,
  });

  useEffect(() => {
    let cancelled = false;

    const pickAndLoadGrace = async () => {
      try {
        if (!user?.id) return;
        if (isOnBingoRoom) {
          setBingoGraceInfo(null);
          setBingoGraceRemainingMs(0);
          return;
        }

        const first = Array.isArray(activeBingoRooms) && activeBingoRooms.length > 0 ? activeBingoRooms[0] : null;
        if (!first || !first.code) {
          setBingoGraceInfo(null);
          setBingoGraceRemainingMs(0);
          return;
        }

        const graceRes = await axios.get(`/api/bingo/v2/rooms/${first.code}/grace`);
        const data = graceRes.data || {};

        if (cancelled) return;

        const serverNowMs = data.serverNow ? new Date(data.serverNow).getTime() : Date.now();
        const clientNowMs = Date.now();
        const offsetMs = serverNowMs - clientNowMs;
        setBingoGraceOffsetMs(offsetMs);

        if (!data.active || !data.expiresAt) {
          setBingoGraceInfo({
            roomCode: first.code,
            status: first.status,
            active: false,
            path: first.status === 'waiting' ? `/bingo/v2/room/${first.code}` : `/bingo/v2/play/${first.code}`,
          });
          setBingoGraceRemainingMs(0);
          return;
        }

        const expiresAtMs = new Date(data.expiresAt).getTime();
        const remainingMs = Math.max(0, expiresAtMs - (Date.now() + offsetMs));

        setBingoGraceInfo({
          roomCode: first.code,
          status: data.status || first.status,
          active: true,
          expiresAt: data.expiresAt,
          path: (data.status || first.status) === 'waiting'
            ? `/bingo/v2/room/${first.code}`
            : `/bingo/v2/play/${first.code}`,
        });
        setBingoGraceRemainingMs(remainingMs);
      } catch (_) {
        if (!cancelled) {
          setBingoGraceInfo(null);
          setBingoGraceRemainingMs(0);
        }
      }
    };

    pickAndLoadGrace();
    return () => {
      cancelled = true;
    };
  }, [user?.id, isOnBingoRoom, activeBingoRooms]);

  useEffect(() => {
    if (!bingoGraceInfo?.active || !bingoGraceInfo?.expiresAt) return;

    const tick = () => {
      const exp = new Date(bingoGraceInfo.expiresAt).getTime();
      const remaining = Math.max(0, exp - (Date.now() + bingoGraceOffsetMs));
      setBingoGraceRemainingMs(remaining);
      if (remaining <= 0) {
        setBingoGraceInfo(null);
      }
    };

    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [bingoGraceInfo?.active, bingoGraceInfo?.expiresAt, bingoGraceOffsetMs]);

  const formatGraceRemaining = (ms) => {
    const totalSec = Math.max(0, Math.ceil(ms / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  useEffect(() => {
    const roomCode = bingoGraceInfo?.roomCode;
    const isActive = !!bingoGraceInfo?.active && !!roomCode && bingoGraceRemainingMs > 0;

    if (!isActive) {
      if (lastBingoGraceToastRef.current?.roomCode) {
        toast.dismiss(`bingo-grace-${lastBingoGraceToastRef.current.roomCode}`);
        lastBingoGraceToastRef.current = null;
      }
      return;
    }

    if (lastBingoGraceToastRef.current?.roomCode !== roomCode) {
      lastBingoGraceToastRef.current = { roomCode };

      toast.custom(
        (t) => (
          <button
            type="button"
            onClick={() => {
              try {
                toast.dismiss(t.id);
              } catch (_) {
                // ignore
              }
              navigate(bingoGraceInfo.path);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'linear-gradient(90deg, rgba(124,58,237,0.95), rgba(236,72,153,0.95))',
              color: 'white',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
              width: '100%',
              maxWidth: 420,
            }}
          >
            <span style={{ flex: 1, textAlign: 'left' }}>
              Volver a la sala #{roomCode}
            </span>
            <span style={{ opacity: 0.9, fontVariantNumeric: 'tabular-nums' }}>
              {formatGraceRemaining(bingoGraceRemainingMs)}
            </span>
          </button>
        ),
        {
          id: `bingo-grace-${roomCode}`,
          duration: Infinity,
          position: 'bottom-center',
        }
      );
    }

    if (bingoGraceRemainingMs <= 0) {
      toast.dismiss(`bingo-grace-${roomCode}`);
      lastBingoGraceToastRef.current = null;
    }
  }, [bingoGraceInfo?.active, bingoGraceInfo?.roomCode, bingoGraceInfo?.path, bingoGraceRemainingMs, navigate]);

  const getStoredGreenButtonAction = () => {
    if (!user?.id || typeof window === 'undefined') return '';
    try {
      const key = `green_button_action_${user.id}`;
      const stored = window.localStorage.getItem(key);
      return stored && typeof stored === 'string' ? stored : '';
    } catch {
      return '';
    }
  };

  const setStoredGreenButtonAction = (value) => {
    if (!user?.id || typeof window === 'undefined') return;
    try {
      const key = `green_button_action_${user.id}`;
      window.localStorage.setItem(key, value || '');
    } catch {
      // ignore
    }
  };

  const executeGreenButtonAction = (action) => {
    if (!user) {
      navigate('/profile');
      return;
    }

    if (action === 'pay') {
      setShowQrPaymentModal(true);
      return;
    }

    if (action === 'pos') {
      if (homeSlug) {
        if (!isGlobalAdmin && homeStaffLoading) {
          toast('Verificando acceso a tu tienda...');
          return;
        }
        if (isGlobalAdmin || canAccessStorePOS(homeStaffData)) {
          navigate(`/store/${homeSlug}/pos`);
        } else {
          toast.error('No tienes acceso al POS de tu tienda');
          navigate('/lobby', { replace: true });
        }
      } else {
        navigate('/profile?greenTarget=pos');
      }
      return;
    }

    if (action === 'store_dashboard') {
      if (homeSlug) {
        if (!isGlobalAdmin && homeStaffLoading) {
          toast('Verificando acceso a tu tienda...');
          return;
        }
        if (isGlobalAdmin || canAccessStoreDashboard(homeStaffData)) {
          navigate(`/store/${homeSlug}/dashboard`);
        } else {
          toast.error('No tienes acceso al panel de tienda');
          navigate('/lobby', { replace: true });
        }
      } else {
        navigate('/profile?greenTarget=store_dashboard');
      }
      return;
    }

    if (action === 'transport') {
      setTransportAutoLocateToken(Date.now());
      setShowTransportRideModal(true);
      return;
    }

    navigate('/profile');
  };

  const handleGreenBadgeClick = () => {
    if (greenButtonLongPressTriggeredRef.current) {
      greenButtonLongPressTriggeredRef.current = false;
      return;
    }

    const action = normalizeGreenButtonAction(getStoredGreenButtonAction());
    executeGreenButtonAction(action);
  };

  const handleGreenBadgePointerDown = () => {
    if (!user) return;
    if (greenButtonLongPressTimeoutRef.current) {
      clearTimeout(greenButtonLongPressTimeoutRef.current);
      greenButtonLongPressTimeoutRef.current = null;
    }
    greenButtonLongPressTriggeredRef.current = false;
    greenButtonLongPressTimeoutRef.current = setTimeout(() => {
      greenButtonLongPressTriggeredRef.current = true;
      setShowGreenButtonPickerModal(true);
    }, 1000);
  };

  const clearGreenBadgeLongPress = () => {
    if (greenButtonLongPressTimeoutRef.current) {
      clearTimeout(greenButtonLongPressTimeoutRef.current);
      greenButtonLongPressTimeoutRef.current = null;
    }
  };

  const normalizeGreenButtonAction = (action) => {
    if (action === 'pos' && !canUseStorePosAction) return '';
    if (action === 'store_dashboard' && !canUseStoreDashboardAction) return '';
    return action;
  };

  const headerLogoSrc =
    resolveImageUrlBusted(
      activeStoreBranding?.logo_url,
      activeStoreBranding?.updated_at || activeStoreBranding?.id || activeStoreSlug
    ) || resolveImageUrl(activeStoreBranding?.logo_url) || '/logo.ico';

  const getGreenButtonEmoji = (action) => {
    if (action === 'transport') return '\u{1F697}';
    if (action === 'pay') return '\u{1F4B5}';
    if (action === 'pos') return '\u{1F3EA}';
    if (action === 'store_dashboard') return '\u{1F4C8}';
    return '\u{1F7E2}';
  };

  const handleMainScroll = (e) => {
    // Scroll tracking removed for fixed floating button
  };

  return (
    <div className="flex flex-col min-h-dvh bg-background-dark">
        {/* Header */}
        <header className="bg-card/90 backdrop-blur-md border-b border-border-glass px-3 py-2 safe-top sticky top-0 z-40 transition-colors">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              className="h-10 min-w-[108px] rounded-full px-3 bg-emerald-50 hover:bg-emerald-100/80 border border-emerald-200 text-emerald-800 shadow-sm transition-all flex items-center justify-between gap-2 active:scale-95 cursor-pointer"
              title="Botón principal XYZ y usuarios conectados"
              onClick={handleGreenBadgeClick}
              onPointerDown={handleGreenBadgePointerDown}
              onPointerUp={clearGreenBadgeLongPress}
              onPointerLeave={clearGreenBadgeLongPress}
              onPointerCancel={clearGreenBadgeLongPress}
            >
              <div className="flex items-center gap-2 min-w-0">
                <img
                  src={headerLogoSrc}
                  alt={activeStoreBranding?.name ? `Logo de ${activeStoreBranding.name}` : 'XYZ Logo'}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/logo.ico';
                  }}
                  className="w-5 h-5 object-contain shrink-0"
                />
                <span className="text-base md:text-lg leading-none">{getGreenButtonEmoji(normalizeGreenButtonAction(getStoredGreenButtonAction()))}</span>
              </div>
              <span className="px-2 py-0.5 rounded-full bg-emerald-100/80 text-emerald-900 text-[10px] md:text-xs font-bold leading-none tabular-nums">
                {onlineUsers}
              </span>
            </button>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {/* Acceso dinámico a pestaña Hospedaje en vista POS */}
              {isPosRoute && isLodgingStore && (
                <button
                  type="button"
                  onClick={() => navigate(`/store/${encodeURIComponent(activeStoreSlug)}/dashboard?tab=rooms`)}
                  className="px-3 py-1 rounded-full bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 flex items-center gap-1.5 text-[11px] md:text-xs font-bold shadow-sm hover:scale-105 active:scale-95 transition-all cursor-pointer"
                  title="Ir al panel de Hospedaje (Habitaciones y Reservas)"
                >
                  <Bed size={15} className="text-blue-600 shrink-0" />
                  <span>Hospedaje</span>
                </button>
              )}

              {/* Desaparecidos / Emergencia Link */}
              <Link
                to="/desaparecidosvenezuela"
                style={{
                  background: 'rgba(239,68,68,0.15)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  color: '#ef4444',
                  padding: '5px 12px',
                  borderRadius: '999px',
                  textDecoration: 'none',
                  fontSize: '11px',
                  fontWeight: '700',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s',
                }}
                className="hover:scale-105 transition-transform"
                title="Desaparecidos Venezuela"
              >
                🇻🇪 Emergencia
              </Link>

              {/* Combined BCV & Music Player */}
              <button
                type="button"
                className="badge-coins cursor-pointer hover:scale-105 active:scale-95 transition-all flex items-center gap-1.5 shadow-sm"
                onClick={() => {
                  setShowMusicPlayer(true);
                  setShowBcvCalculator(true);
                }}
                title="Tasa BCV / Reproductor"
              >
                {vesPerUsdt && (
                  <span className="text-[10px] font-bold text-amber-900">
                    {vesPerUsdt.toLocaleString('es-VE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </span>
                )}
                <div className="w-[1px] h-3 bg-amber-400/40" />
                <Volume2
                  size={14}
                  className={
                    musicUiState?.hasTrack && Number(musicUiState?.volume || 0) > 0
                      ? 'text-emerald-700'
                      : 'text-amber-800'
                  }
                />
              </button>

              {/* Message Inbox Button */}
              <MessageInbox disablePrefetch={disableInboxPrefetch} />

              {/* Global Cart Button */}
              <button
                type="button"
                className="badge-experience relative cursor-pointer hover:scale-105 active:scale-95 transition-all shadow-sm"
                onClick={() => setShowGlobalCart(true)}
                title="Mi Bolso"
              >
                <ShoppingCart size={18} className={totalItems > 0 ? 'text-[#1A73E8]' : 'text-slate-600'} />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#D93025] text-white text-[10px] font-bold h-4 w-4 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                    {totalItems}
                  </span>
                )}
              </button>
            </div>
          </div>
      </header>

      {/* Main Content */}
      <main 
        ref={mainRef}
        className="flex-1 pb-28 md:pb-32 scrollbar-thin overflow-y-auto"
      >
        <Outlet />
      </main>

      <WelcomeOverlay />

      {activeRideRequest && !showTransportRideModal && (
        <button
          type="button"
          className="fixed left-3 bottom-24 z-40 h-12 w-12 rounded-full bg-orange-500 text-white shadow-lg shadow-orange-500/30 flex items-center justify-center"
          title="Solicitud de transporte activa"
          onClick={() => setShowTransportRideModal(true)}
        >
          🚗
        </button>
      )}

      {canSeeGamesAndRaffles && bingoGraceInfo?.active && bingoGraceInfo?.roomCode && bingoGraceRemainingMs > 0 && (
        <button
          type="button"
          className="fixed right-3 bottom-24 z-40 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/30 px-4 h-12 flex items-center gap-2"
          title="Volver a la sala"
          onClick={() => navigate(bingoGraceInfo.path)}
        >
          <span className="text-sm font-semibold">Volver #{bingoGraceInfo.roomCode}</span>
          <span className="text-xs opacity-90">{formatGraceRemaining(bingoGraceRemainingMs)}</span>
        </button>
      )}

      {/* Shared Utility Overlay (BCV Calculator + Music Player) */}
      <AnimatePresence>
        {(showBcvCalculator || showMusicPlayer) && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center overflow-y-auto overscroll-y-contain bg-black/60 backdrop-blur-sm px-4 py-4 sm:py-6"
            onClick={() => {
              setShowBcvCalculator(false);
              setShowMusicPlayer(false);
            }}
          >
            <div className="flex w-full max-w-sm flex-col gap-4 self-end sm:self-auto max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)] overflow-y-auto overscroll-y-contain" onClick={(e) => e.stopPropagation()}>
              {/* BCV Calculator Card */}
              {showBcvCalculator && vesPerUsdt && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="card-glass w-full max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)] overflow-y-auto overscroll-y-contain p-4 rounded-xl space-y-3 text-xs shrink-0"
                >
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold">Calculadora rápida Bs</h2>
                    <button
                      type="button"
                      className="text-[11px] text-text/60 hover:text-text"
                      onClick={() => {
                        setShowBcvCalculator(false);
                        setShowMusicPlayer(false);
                      }}
                    >
                      Cerrar ambos
                    </button>
                  </div>

                  <p className="text-[11px] text-text/60">
                    Convierte usando la tasa BCV actual.
                  </p>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (bcvCalculatorMode !== 'multiply') {
                          setBcvCalculatorMode('multiply');
                          setBcvCalculatorInput('');
                          setBcvCopied(false);
                        }
                      }}
                      className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-colors ${
                        bcvCalculatorMode === 'multiply'
                          ? 'bg-accent/20 text-accent'
                          : 'bg-glass text-text/70 hover:bg-glass/70'
                      }`}
                    >
                      Multiplicar
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (bcvCalculatorMode !== 'divide') {
                          setBcvCalculatorMode('divide');
                          setBcvCalculatorInput('');
                          setBcvCopied(false);
                        }
                      }}
                      className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-colors ${
                        bcvCalculatorMode === 'divide'
                          ? 'bg-accent/20 text-accent'
                          : 'bg-glass text-text/70 hover:bg-glass/70'
                      }`}
                    >
                      Dividir
                    </button>
                  </div>

                  <div className="space-y-1">
                    <div className="text-text/60">
                      {bcvCalculatorMode === 'divide' ? 'Monto base (ej. Bs)' : 'Monto base (ej. USD)'}
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={bcvCalculatorInput}
                      onChange={(e) => {
                        setBcvCalculatorInput(e.target.value);
                        if (bcvCopied) setBcvCopied(false);
                      }}
                      className="input-glass w-full"
                      placeholder={bcvCalculatorMode === 'divide' ? 'Ej: 1000' : 'Ej: 10'}
                    />
                  </div>

                  {(() => {
                    const parsed = parseFloat(bcvCalculatorInput || '0');
                    const isValid = Number.isFinite(parsed) && parsed >= 0;
                    const result = isValid
                      ? bcvCalculatorMode === 'divide'
                        ? parsed / vesPerUsdt
                        : parsed * vesPerUsdt
                      : 0;
                    const isCopyDisabled = !isValid || result <= 0;
                    return (
                      <div className="mt-2 p-2.5 rounded-lg bg-glass flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <div className="text-[11px] text-text/60">
                            Resultado
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCopyBcvResult(result, isValid)}
                            disabled={isCopyDisabled}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                              bcvCopied
                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                : 'bg-white/10 hover:bg-white/15 text-text/90 hover:text-white border border-white/10'
                            } disabled:opacity-30 disabled:pointer-events-none`}
                            title="Copiar monto"
                          >
                            {bcvCopied ? (
                              <>
                                <Check size={12} className="text-emerald-400" />
                                <span>¡Copiado!</span>
                              </>
                            ) : (
                              <>
                                <Copy size={12} />
                                <span>Copiar</span>
                              </>
                            )}
                          </button>
                        </div>
                        <div className="text-sm font-semibold text-text/90">
                          {isValid
                            ? result.toLocaleString('es-VE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }) + (bcvCalculatorMode === 'divide' ? ' USD' : ' Bs')
                            : bcvCalculatorMode === 'divide'
                            ? '0,00 USD'
                            : '0,00 Bs'}
                        </div>
                        <div className="text-[10px] text-text/50">
                          Tasa BCV: {vesPerUsdt.toLocaleString('es-VE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })} Bs
                        </div>
                      </div>
                    );
                  })()}

                  <div className="grid grid-cols-2 gap-2">
                    {/* Tarjeta Fuegos */}
                    <div className="rounded-xl border border-fire-orange/20 bg-fire-orange/5 p-3 flex flex-col justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-text/50">Fuegos</div>
                        <div className="text-sm font-semibold text-fire-orange mb-2">🔥 {displayFires.toFixed(2)}</div>
                      </div>
                      <div className="grid grid-cols-3 gap-1 mt-auto">
                        <button
                          type="button"
                          className="w-full rounded-md bg-emerald-500/10 py-1 px-1 text-[10px] font-semibold text-emerald-300 hover:bg-emerald-500/20 transition-colors text-center"
                          onClick={() => {
                            setShowBuyFires(true);
                            setShowBcvCalculator(false);
                            setShowMusicPlayer(false);
                          }}
                        >
                          Comprar
                        </button>
                        <button
                          type="button"
                          className="w-full rounded-md bg-cyan-500/10 py-1 px-1 text-[10px] font-semibold text-cyan-300 hover:bg-cyan-500/20 transition-colors text-center"
                          onClick={() => {
                            setWalletHistoryInitialTab('fires');
                            setShowWalletHistoryModal(true);
                            setShowBcvCalculator(false);
                            setShowMusicPlayer(false);
                          }}
                        >
                          Historial
                        </button>
                        <button
                          type="button"
                          className="w-full rounded-md bg-fire-orange/10 py-1 px-1 text-[10px] font-semibold text-fire-orange hover:bg-fire-orange/20 transition-colors text-center"
                          onClick={() => {
                            setShowRedeemModal(true);
                            setShowBcvCalculator(false);
                            setShowMusicPlayer(false);
                          }}
                        >
                          Retirar
                        </button>
                      </div>
                    </div>

                    {/* Tarjeta Monedas */}
                    <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-3 flex flex-col justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-text/50">DataCoins</div>
                        <div className="text-sm font-semibold text-yellow-500 mb-2">💰 {displayCoins.toFixed(2)}</div>
                      </div>
                      <div className="grid grid-cols-1 mt-auto">
                        <button
                          type="button"
                          className="w-full rounded-md bg-cyan-500/10 py-1 text-[10px] font-semibold text-cyan-300 hover:bg-cyan-500/20 transition-colors"
                          onClick={() => {
                            setWalletHistoryInitialTab('coins');
                            setShowWalletHistoryModal(true);
                            setShowBcvCalculator(false);
                            setShowMusicPlayer(false);
                          }}
                        >
                          Historial
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Music Player Card */}
              {showMusicPlayer && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="w-full"
                >
                  <GlobalMusicPlayer
                    isOpen={true}
                    onOpenChange={(val) => {
                      if (!val) {
                        setShowMusicPlayer(false);
                        setShowBcvCalculator(false);
                      }
                    }}
                    onUiStateChange={setMusicUiState}
                    compactMode={true}
                  />
                </motion.div>
              )}
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-t border-theme px-1 sm:px-3 py-1.5 safe-bottom shadow-[0_-2px_10px_rgba(60,64,67,0.08)]">
        <div className="flex justify-around items-center w-full max-w-lg mx-auto text-xs text-center">
          {navItems.map(({ path, icon: Icon, label, tutorialTarget }) => {
            const isActive = location.pathname.startsWith(path);
            return (
              <NavLink
                key={path}
                to={path}
                data-tutorial-target={tutorialTarget || undefined}
                className="flex-1 min-w-0 py-0.5 px-0.5 sm:px-1 flex flex-col items-center justify-center group transition-all select-none"
              >
                <div
                  className={`w-10 sm:w-12 h-6 sm:h-7 rounded-full flex items-center justify-center transition-all ${
                    isActive
                      ? 'bg-[#E8F0FE] text-[#1A73E8] shadow-xs'
                      : 'text-slate-600 group-hover:bg-slate-100 group-hover:text-slate-900'
                  }`}
                >
                  <Icon size={18} className="shrink-0" />
                </div>
                <span
                  className={`block text-[10px] sm:text-[11px] mt-0.5 tracking-tight truncate w-full text-center ${
                    isActive ? 'font-bold text-[#1A73E8]' : 'text-slate-600 group-hover:text-slate-900 font-medium'
                  }`}
                >
                  {label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Experience Modal */}
      <ExperienceModal 
        isOpen={showExperienceModal}
        onClose={() => setShowExperienceModal(false)}
        user={user}
      />

      {/* Buy Experience Modal */}
      <BuyExperienceModal 
        isOpen={showBuyExperienceModal}
        onClose={() => setShowBuyExperienceModal(false)}
        user={user}
      />

      {/* Wallet History Modal */}
      <WalletHistoryModal 
        isOpen={showWalletHistoryModal}
        onClose={() => setShowWalletHistoryModal(false)}
        onBack={() => {
          setShowWalletHistoryModal(false);
          setShowMusicPlayer(true);
          setShowBcvCalculator(true);
        }}
        onOpenSend={() => {
          setShowWalletHistoryModal(false);
          setShowSendFires(true);
        }}
        onOpenBuy={() => {
          setShowWalletHistoryModal(false);
          setShowBuyFires(true);
        }}
        onOpenReceive={() => {
          setShowWalletHistoryModal(false);
          setShowReceiveFires(true);
        }}
        onOpenWithdraw={() => {
          setShowWalletHistoryModal(false);
          setShowRedeemModal(true);
        }}
        initialTab={walletHistoryInitialTab}
      />

      <RedeemModal
        isOpen={showRedeemModal}
        onClose={() => setShowRedeemModal(false)}
        user={user}
        refreshUser={refreshUser}
        fiatContext={fiatContext}
        onBack={() => {
          setShowRedeemModal(false);
          setShowWalletHistoryModal(true);
        }}
      />

      <SendFiresModal
        isOpen={showSendFires}
        onClose={() => setShowSendFires(false)}
        onBack={() => {
          setShowSendFires(false);
          setShowWalletHistoryModal(true);
        }}
      />

      <BuyFiresModal
        isOpen={showBuyFires}
        onClose={() => setShowBuyFires(false)}
        onBack={() => {
          setShowBuyFires(false);
          setShowWalletHistoryModal(true);
        }}
      />

      <ReceiveFiresModal
        isOpen={showReceiveFires}
        onClose={() => setShowReceiveFires(false)}
        walletAddress={user?.wallet_address}
        onBack={() => {
          setShowReceiveFires(false);
          setShowWalletHistoryModal(true);
        }}
      />

      {/* QR Payment Link Modal (BotÃ³n verde  Pagar ) */}
      <QrPaymentLinkModal
        isOpen={showQrPaymentModal}
        onClose={() => setShowQrPaymentModal(false)}
      />

      {showGreenButtonPickerModal && user && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={() => setShowGreenButtonPickerModal(false)}
        >
          <div
            className="card-glass p-4 rounded-xl max-w-xs w-full space-y-3 text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">Botón verde</h2>
              <button
                type="button"
                className="text-[11px] text-text/60 hover:text-text"
                onClick={() => setShowGreenButtonPickerModal(false)}
              >
                Cerrar
              </button>
            </div>

            {(() => {
              const currentAction = normalizeGreenButtonAction(getStoredGreenButtonAction());
              const options = [
                { value: '', label: 'Sin acción especial' },
                { value: 'pay', label: '\u{1F4B5} Pagar (scanner QR)' },
                ...(canUseStorePosAction ? [{ value: 'pos', label: '\u{1F3EA} POS de mi tienda' }] : []),
                ...(canUseStoreDashboardAction ? [{ value: 'store_dashboard', label: '\u{1F4C8} Panel de tienda' }] : []),
                { value: 'transport', label: '\u{1F697} Transporte' },
                { value: 'switch_account', label: '\u{1F465} Cambiar de cuenta' }
              ];

              return (
                <div className="flex flex-col gap-2">
                  {options.map((opt) => {
                    const selected = opt.value === currentAction;
                    return (
                      <button
                        key={opt.value || 'none'}
                        type="button"
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          selected
                            ? 'bg-orange-500/20 text-orange-300'
                            : 'bg-glass hover:bg-white/10 text-text/90'
                        }`}
                        onClick={() => {
                          if (opt.value === 'switch_account') {
                            setShowGreenButtonPickerModal(false);
                            setShowAccountSwitchModal(true);
                            return;
                          }
                          setStoredGreenButtonAction(opt.value);
                          setShowGreenButtonPickerModal(false);
                          executeGreenButtonAction(opt.value);
                        }}
                      >
                        <div className="text-[12px] font-semibold">{opt.label}</div>
                        {selected && (
                          <div className="text-[10px] text-text/60 mt-0.5">Seleccionado</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <TransportRideModal
        isOpen={showTransportRideModal}
        autoLocateOnOpenToken={transportAutoLocateToken}
        onClose={() => setShowTransportRideModal(false)}
      />

      {/* Unified Chat System */}
      {!hideChat && <UnifiedChat activeTransportRequest={activeRideRequest} />}

      {/* Push Notification Permission Modal */}
      {showPushPermissionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={() => setShowPushPermissionModal(false)}
        >
          <div
            className="card-glass p-4 rounded-xl max-w-xs w-full space-y-3 text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">
                {isIOSPromptNeeded ? 'Notificaciones en iPhone' : 'Activar notificaciones'}
              </h2>
              <button
                type="button"
                className="text-[11px] text-text/60 hover:text-text"
                onClick={() => setShowPushPermissionModal(false)}
              >
                Cerrar
              </button>
            </div>

            <p className="text-[11px] text-text/60">
              {isIOSPromptNeeded
                ? 'Para recibir notificaciones en tu iPhone, agrega MundoXYZ a tu pantalla de inicio desde el menú Compartir.'
                : 'Recibe notificaciones push cuando tengas mensajes nuevos en tu buzón y actualizaciones de pedidos.'}
            </p>

            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 px-3 py-2 rounded-lg bg-glass hover:bg-glass/70 text-xs"
                onClick={() => {
                  setShowPushPermissionModal(false);
                  if (typeof window !== 'undefined') {
                    window.localStorage.setItem('push_permission_modal_shown', 'true');
                  }
                }}
              >
                Ahora no
              </button>
              {isIOSPromptNeeded ? (
                <button
                  type="button"
                  className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 text-white font-semibold text-xs transition-all shadow-md shadow-violet-600/30"
                  onClick={() => {
                    setShowPushPermissionModal(false);
                    if (typeof window !== 'undefined') {
                      window.localStorage.setItem('push_permission_modal_shown', 'true');
                    }
                    setShowIosInstallModal(true);
                  }}
                >
                  Instalar 📲
                </button>
              ) : (
                <button
                  type="button"
                  className="flex-1 px-3 py-2 rounded-lg bg-accent text-dark font-semibold text-xs hover:opacity-95"
                  onClick={async () => {
                    setShowPushPermissionModal(false);
                    if (typeof window !== 'undefined') {
                      window.localStorage.setItem('push_permission_modal_shown', 'true');
                    }
                    try {
                      const res = await requestPermissionAndRegister();
                      if (res?.ok) {
                        toast.success('Notificaciones activadas');
                        return;
                      }
                      if (res?.reason === 'ios_pwa_required') {
                        setShowIosInstallModal(true);
                        return;
                      }
                      if (res?.reason === 'missing_firebase_env') {
                        toast('Notificaciones permitidas, pero falta configurar Firebase (FCM)');
                        return;
                      }
                      if (res?.reason === 'missing_webpush_vapid') {
                        toast('Notificaciones no disponibles en este momento (configuración pendiente)');
                        return;
                      }
                      if (res?.reason === 'backend_push_disabled') {
                        toast('El servicio de notificaciones está desactivado');
                        return;
                      }
                      if (res?.reason === 'backend_error') {
                        toast.error('Error al registrar en el servidor');
                        return;
                      }
                      if (res?.reason === 'no_token') {
                        toast.error('No se pudo obtener token de notificación');
                        return;
                      }
                      if (res?.reason === 'not_granted') {
                        toast('Para activar notificaciones, ve a la configuración de tu navegador');
                        return;
                      }
                      if (res?.reason === 'not_supported') {
                        toast('Tu navegador no soporta notificaciones push');
                        return;
                      }
                      toast.error('No se pudo activar notificaciones');
                    } catch {
                      toast.error('Error al solicitar permisos');
                    }
                  }}
                >
                  Activar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* iOS PWA Install Guidance Modal */}
      <IosInstallPromptModal
        isOpen={showIosInstallModal}
        onClose={() => setShowIosInstallModal(false)}
      />

      <GlobalCartDrawer
        show={showGlobalCart}
        onClose={() => setShowGlobalCart(false)}
        rates={{
          bs: vesPerUsdt || 38.5,
          fires: fiatContext?.config?.fires_per_usdt || 100
        }}
      />

      {/* Tutorial Overlay - guides new users */}
      <TutorialOverlay />

      {/* Tutorial auto-trigger on login for new users */}
      <TutorialAutoTrigger />

      <AccountSwitchModal
        isOpen={showAccountSwitchModal}
        onClose={() => setShowAccountSwitchModal(false)}
      />
    </div>
  );
};

export default Layout;

